import { ecId, ecLean, scalar } from '../ability/context.js';
import { readInventoryField, readInventoryRows } from '../compile/inventory-raw-readers.js';
import { equipBefore, projectEquipItemHeld, type EquipPlanRow, type HandSlots } from './equip-plan.js';
import { abilityEntryId, CHARGE_BUCKETS, type ChargeReaders } from '../charges/charge-adjust.js';
import { applyResourceValues, type ResourceValues } from '../resources/resource-adjust.js';
import type { LongRestCharge } from '../turns/long-rest.js';
import { posCoord } from '../reactions/php-row-rules.js';
import { orbQueueText } from '../ability/orbs/orb-queue.js';

type Row = Record<string, unknown>;
type Path = Array<string | number>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const copyScalars = (value: unknown): Row => Object.fromEntries(Object.entries(record(value) ?? {}).map(([key, cell]) => [key, Array.isArray(cell) ? cell.slice() : cell]));

/** A decision borrows the held graph. Only a named mutation owns new ancestors; definitions are always borrowed.
 * Keep this view local to one decision. Durable before facts are scalar projections, never a retained trial frame. */
export function inventoryDecisionView(source: ReadonlyMap<string, Row>) {
  const members = new Map(source);
  const items = new Map<string, Array<{ actorId: string; path: Path }>>();
  const owned = new WeakSet<object>();
  const index = (actorId: string, nodes: unknown, path: Path, seen: Set<object>): void => {
    if (!nodes || typeof nodes !== 'object' || seen.has(nodes)) return;
    seen.add(nodes);
    for (const [key, value] of Object.entries(nodes)) {
      const entry = record(value);
      if (!entry) continue;
      const at = [...path, key], id = upper(readInventoryField(entry, 'inventory_id'));
      if (id) { const locations = items.get(id) ?? []; locations.push({ actorId, path: at }); items.set(id, locations); }
      index(actorId, entry.inventory_in_container, [...at, 'inventory_in_container'], seen);
    }
  };
  for (const [actorId, member] of members) index(actorId, member.inventory, ['inventory'], new Set());
  const own = (value: unknown): Row | unknown[] => {
    if (value && typeof value === 'object' && owned.has(value)) return value as Row | unknown[];
    const result = Array.isArray(value) ? value.slice() : Object.assign({}, record(value));
    owned.add(result);
    return result;
  };
  const edit = (actorId: string, path: Path, update: (value: unknown) => unknown): void => {
    const original = members.get(actorId);
    if (!original) return;
    const root = own(original) as Row;
    members.set(actorId, root);
    let cursor = root;
    for (const key of path.slice(0, -1)) {
      const child = own(cursor[key]); cursor[key] = child; cursor = child as Row;
    }
    const last = path[path.length - 1]!;
    cursor[last] = update(cursor[last]);
  };
  const item = (id: string): Row | null => {
    const at = items.get(upper(id))?.[0];
    return at ? at.path.reduce<unknown>((value, key) => (value as Row | undefined)?.[key], members.get(at.actorId)) as Row : null;
  };
  const replaceItem = (id: string, update: (row: Row) => Row): void => {
    for (const at of items.get(upper(id)) ?? []) edit(at.actorId, at.path, value => update(record(value)!));
  };
  const pinItems = (ids: Iterable<string>): void => {
    for (const id of ids) replaceItem(id, entry => ({ ...entry, inventory: copyScalars(entry.inventory) }));
  };
  return {
    members, item, replaceItem, pinItems,
    held(actorId: string, inventoryId: string): Row | null {
      const at = items.get(upper(inventoryId))?.find(location => location.actorId === actorId), entry = item(inventoryId);
      if (!at || !entry) return null;
      // The shared unit projection needs only this item. Give it no unrelated container branches to visit/copy.
      const projected = projectEquipItemHeld({ inventory: [entry] }, inventoryId, actorId);
      if (!projected) return null;
      const key = at.path[at.path.length - 1]!;
      edit(actorId, at.path.slice(0, -1), value => {
        if (Array.isArray(value)) return value.filter((_, index) => index !== Number(key));
        const list = Object.assign({}, value); delete (list as Row)[key]; return list;
      });
      edit(actorId, ['inventory'], value => [...readInventoryRows({ inventory: value }, 'inventory'), ...readInventoryRows(projected, 'inventory')]);
      // Removal shifts sibling indices too. Reindex this member's borrowed rows without copying them.
      for (const [id, locations] of items) {
        const retained = locations.filter(location => location.actorId !== actorId);
        if (retained.length) items.set(id, retained); else items.delete(id);
      }
      index(actorId, members.get(actorId)!.inventory, ['inventory'], new Set());
      return members.get(actorId)!;
    },
    /** Pin the scalar facts equipBefore/rules consume before timed planning yields. Other inventory and catalog branches retain identity. */
    equipment(actorId: string, slots: HandSlots, inventoryId = ''): Row {
      const row = members.get(actorId)!;
      const before = equipBefore(row, slots, inventoryId);
      pinItems(before.rows.map(fact => fact.inventory_id));
      edit(actorId, ['encounter_creatures'], copyScalars);
      const computed = record(row.computed), creature = record(computed?.creature);
      if (creature) edit(actorId, ['computed', 'creature'], value => Object.assign({}, value));
      const statuses = record(row.statuses);
      if (statuses) for (const mode of ['target', 'caster']) {
        const list = statuses[mode];
        if (list && typeof list === 'object') edit(actorId, ['statuses', mode], value => Array.isArray(value) ? value.slice() : Object.assign({}, value));
      }
      return members.get(actorId)!;
    },
    equipped(rows: readonly EquipPlanRow[]): void {
      for (const change of rows) replaceItem(change.inventory_id, entry => {
        const { inventory_id: _id, split_from: _split, ...values } = change;
        const bucket: Row = { ...record(entry.inventory), ...values };
        if (!change.equipped) delete bucket.equipped;
        else bucket.equipped = upper(change.equipped);
        if (change.possess_id) bucket.possess_id = upper(change.possess_id);
        return { ...entry, inventory: bucket };
      });
    },
    /** The rest compile consumes changed resources and pools; unrelated statuses, inventories and ability definitions stay shared. */
    rest(actorId: string, values: ResourceValues, pools: readonly LongRestCharge[], read: ChargeReaders): Row {
      const columns = Object.keys(values);
      if (columns.length) {
        edit(actorId, ['encounter_creatures'], copyScalars);
        if (columns.some(key => ['v_current', 'v_temp', 'v_max', 'v_max_temp'].includes(key)) && record(record(members.get(actorId)?.computed)?.vitality))
          edit(actorId, ['computed', 'vitality'], value => Object.assign({}, value));
        applyResourceValues(members.get(actorId)!, values, { layer: read.value, hasLayer: (r, key) => read.value(r, key) != null });
      }
      const abilities = new Map(pools.filter(pool => pool.kind === 'ability').map(pool => [pool.item_id, pool.to]));
      const changed = new Map<object, Row>();
      if (abilities.size) for (const key of CHARGE_BUCKETS) {
        const row = members.get(actorId)!;
        const entries = read.entries(row, key);
        if (!entries.some(entry => abilities.has(abilityEntryId(entry)))) continue;
        const next = entries.map(value => {
          const entry = record(value), current = abilities.get(abilityEntryId(value));
          if (!entry || current === undefined) return value;
          let copy = changed.get(entry);
          if (!copy) {
            copy = { ...entry };
            if (record(entry.charges)) copy.charges = { ...record(entry.charges), current };
            else if ('ability_charges' in entry) copy.ability_charges = current;
            else copy.charges = { current };
            changed.set(entry, copy);
          }
          return copy;
        });
        const compiled = record(record(record(row.computed)?.creature)?.abilities);
        if (compiled && Array.isArray(compiled[key])) {
          // entries() can suppress overridden abilities. A charge change must retain those untouched stored entries.
          const stored = compiled[key] as unknown[];
          edit(actorId, ['computed', 'creature', 'abilities', key], () => stored.map(value => changed.get(value as object) ?? value));
        } else {
          const layers = record(row.item_layer_types), path = layers && key in layers ? ['item_layer_types', key] : [key];
          const stored = layers && key in layers ? layers[key] : row[key];
          if (Array.isArray(stored) && stored.some(value => changed.has(value as object)))
            edit(actorId, path, () => stored.map(value => changed.get(value as object) ?? value));
          else if (stored && typeof stored === 'object' && changed.has(stored)) edit(actorId, path, () => changed.get(stored));
          else edit(actorId, ['item_layer_types', key], () => next);
        }
      }
      // Runtime lists may share an entry across root and compiled buckets. Redirect those changed aliases together;
      // never follow item_arr/catalog into an authored definition just to update a runtime charge wrapper.
      if (changed.size) for (const key of CHARGE_BUCKETS) for (const path of [[key], ['item_layer_types', key], ['computed', 'creature', 'abilities', key]]) {
        const slot = path.reduce<unknown>((value, part) => record(value)?.[part], members.get(actorId));
        if (Array.isArray(slot) && slot.some(value => value && typeof value === 'object' && changed.has(value)))
          edit(actorId, path, () => slot.map(value => changed.get(value) ?? value));
        else if (slot && typeof slot === 'object' && changed.has(slot)) edit(actorId, path, () => changed.get(slot));
      }
      for (const pool of pools) if (pool.kind === 'inventory') replaceItem(pool.holder, entry => ({ ...entry,
        item_layer_types: { ...record(entry.item_layer_types), current_item_charges: pool.to } }));
      return members.get(actorId)!;
    },
  };
}

/** commitZoneEntryVerdict reads only changed EC columns from its before map, after its hash await. */
export function inventoryDecisionBefore(members: ReadonlyMap<string, Row>, changes: Record<string, Row>): Map<string, Row> {
  return new Map(Object.entries(changes).flatMap(([id, values]) => {
    const row = members.get(id);
    if (!row) return [];
    const fields = Object.fromEntries(Object.keys(values).map(key => [key, key === 'orb_queue' ? orbQueueText(ecLean(row, key)) : scalar(ecLean(row, key))]));
    return [[id, { encounter_creatures: { encounter_creature_id: ecId(row), encounter_id: scalar(ecLean(row, 'encounter_id')), ...fields },
      ...(Object.keys(values).some(key => ['x_pos', 'y_pos', 'z_pos'].includes(key))
        ? { position: Object.fromEntries(['x_pos', 'y_pos', 'z_pos'].map(key => [key, posCoord(row, key as 'x_pos' | 'y_pos' | 'z_pos')])) } : {}) }]];
  }));
}
